import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  formatSubscriptionAmount,
  formatSubscriptionDate,
} from "../subscriptionFormatters";

const { TextArea } = Input;
const { Text } = Typography;

type BillingFrequency = "monthly" | "quarterly" | "semiannual" | "annual";

interface InvoiceSummary {
  id: number;
  invoice_number: string;
}

interface InvoiceLineItem {
  item_type: string;
  unit_price: number | string;
}

interface InvoiceDetail extends InvoiceSummary {
  period_start: string;
  period_end: string;
  due_date: string;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  paid_amount?: number;
  currency: string;
  status: string;
  payment_status: string;
  metadata?: unknown;
  line_items?: InvoiceLineItem[];
}

interface CorrectionFormValues {
  period_start: Dayjs;
  due_date: Dayjs;
  billing_frequency: BillingFrequency;
  warehouse_count: number;
  warehouse_unit_price: number;
  correction_reason: string;
}

interface Props {
  open: boolean;
  invoice: InvoiceSummary | null;
  onClose: () => void;
  onSuccess: () => void;
}

const frequencyOptions = [
  { value: "monthly", label: "Mensuelle" },
  { value: "quarterly", label: "Trimestrielle" },
  { value: "semiannual", label: "Semestrielle" },
  { value: "annual", label: "Annuelle" },
];

const frequencyMonths: Record<BillingFrequency, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};

const addBillingMonths = (start: Dayjs, months: number) => {
  const shifted = start.add(months, "month");
  return start.date() === start.daysInMonth()
    ? shifted.endOf("month").startOf("day")
    : shifted;
};

const parseMetadata = (value: unknown): Record<string, any> => {
  if (value && typeof value === "object") return value as Record<string, any>;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const inferFrequency = (invoice: InvoiceDetail, metadata: Record<string, any>) => {
  const configured = String(metadata.billing_frequency || "").toLowerCase();
  if (["monthly", "quarterly", "semiannual", "annual"].includes(configured)) {
    return configured as BillingFrequency;
  }
  const durationMonths = Math.max(
    1,
    dayjs(invoice.period_end).diff(dayjs(invoice.period_start), "month"),
  );
  if (durationMonths >= 12) return "annual";
  if (durationMonths >= 6) return "semiannual";
  if (durationMonths >= 3) return "quarterly";
  return "monthly";
};

const SubscriptionInvoiceCorrectionModal: React.FC<Props> = ({
  open,
  invoice,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<CorrectionFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [annualDiscountMonths, setAnnualDiscountMonths] = useState(1);
  const values = Form.useWatch([], form) as
    | Partial<CorrectionFormValues>
    | undefined;

  const loadDetail = useCallback(async () => {
    if (!invoice?.id) return;
    setLoading(true);
    setDetail(null);
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "Content-Type": "application/json",
      };
      const [response, settingsResponse] = await Promise.all([
        fetch(`/api/subscription/invoices/${invoice.id}`, { headers }),
        fetch("/api/subscription/billing-settings", { headers }),
      ]);
      const [payload, settingsPayload] = await Promise.all([
        response.json(),
        settingsResponse.json(),
      ]);
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Impossible de charger la facture");
      }
      if (!settingsResponse.ok || !settingsPayload.success) {
        throw new Error(
          settingsPayload.message || "Impossible de charger les paramètres",
        );
      }

      const loadedDetail = payload.data.invoice as InvoiceDetail;
      setAnnualDiscountMonths(
        Math.max(
          0,
          Math.min(
            11,
            Number(settingsPayload.data?.settings?.annual_discount_months) || 0,
          ),
        ),
      );
      const metadata = parseMetadata(loadedDetail.metadata);
      const pricingQuote = metadata.pricing_quote || {};
      const subscriptionLines = (loadedDetail.line_items || []).filter(
        (line) => line.item_type === "subscription",
      );
      setDetail(loadedDetail);
      form.setFieldsValue({
        period_start: dayjs(loadedDetail.period_start),
        due_date: dayjs(loadedDetail.due_date),
        billing_frequency: inferFrequency(loadedDetail, metadata),
        warehouse_count:
          Number(pricingQuote.billable_warehouse_count) ||
          subscriptionLines.length ||
          1,
        warehouse_unit_price:
          Number(pricingQuote.warehouse_unit_price) ||
          Number(subscriptionLines[0]?.unit_price) ||
          10000,
        correction_reason: "",
      });
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Impossible de charger la facture",
      );
    } finally {
      setLoading(false);
    }
  }, [form, invoice]);

  useEffect(() => {
    if (open) loadDetail();
    else {
      setDetail(null);
      form.resetFields();
    }
  }, [form, loadDetail, open]);

  const metadata = useMemo(() => parseMetadata(detail?.metadata), [detail]);
  const isHistoricalRegularization = Boolean(
    metadata.historical_regularization ||
      metadata.generated_from === "historical_regularization" ||
      metadata.generated_from === "historical_regularization_correction",
  );
  const isCollected = Boolean(
    detail &&
      (detail.payment_status !== "unpaid" ||
        Number(detail.paid_amount || 0) > 0 ||
        detail.status === "paid"),
  );
  const isCorrectable = Boolean(
    detail &&
      isHistoricalRegularization &&
      !isCollected &&
      !["cancelled", "refunded"].includes(detail.status),
  );
  const preview = useMemo(() => {
    const frequency = values?.billing_frequency || "monthly";
    const months = frequencyMonths[frequency];
    const warehouseCount = Number(values?.warehouse_count || 0);
    const warehouseUnitPrice = Number(values?.warehouse_unit_price || 0);
    const subtotal = Math.max(
      0,
      warehouseCount * warehouseUnitPrice * months,
    );
    const discountedMonths = frequency === "annual" ? annualDiscountMonths : 0;
    const discountAmount = Math.min(
      subtotal,
      warehouseCount * warehouseUnitPrice * discountedMonths,
    );
    const originalTaxableBase = Math.max(
      0,
      Number(detail?.subtotal || 0) - Number(detail?.discount_amount || 0),
    );
    const taxRate = originalTaxableBase
      ? (Number(detail?.tax_amount || 0) * 100) / originalTaxableBase
      : 0;
    const taxAmount = Math.round(
      ((subtotal - discountAmount) * taxRate) / 100,
    );
    const total = Math.round(subtotal - discountAmount + taxAmount);
    const periodEnd = values?.period_start
      ? addBillingMonths(values.period_start, months)
      : null;
    return { discountAmount, discountedMonths, periodEnd, total };
  }, [annualDiscountMonths, detail, values]);

  const submit = useCallback(
    async (values: CorrectionFormValues) => {
      if (!invoice?.id || !isCorrectable) return;
      setSaving(true);
      try {
        const response = await fetch(
          `/api/subscription/invoices/${invoice.id}/correct-regularization`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...values,
              period_start: values.period_start.format("YYYY-MM-DD"),
              due_date: values.due_date.format("YYYY-MM-DD"),
            }),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Correction impossible");
        }
        message.success(payload.message);
        onSuccess();
        onClose();
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Correction impossible",
        );
      } finally {
        setSaving(false);
      }
    },
    [invoice, isCorrectable, onClose, onSuccess],
  );

  return (
    <Modal
      open={open}
      title={
        <span>
          <HistoryOutlined style={{ marginRight: 8 }} />
          Corriger la régularisation — {invoice?.invoice_number || "Facture"}
        </span>
      }
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Annuler et émettre la facture corrigée"
      cancelText="Fermer"
      okButtonProps={{ disabled: !isCorrectable }}
      confirmLoading={saving}
      width={820}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {detail && (
          <>
            <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
              <Descriptions.Item label="Facture originale">
                {detail.invoice_number}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Période actuelle">
                {formatSubscriptionDate(detail.period_start)} – {" "}
                {formatSubscriptionDate(detail.period_end)}
              </Descriptions.Item>
              <Descriptions.Item label="Montant actuel">
                {formatSubscriptionAmount(detail.total_amount, detail.currency)}
              </Descriptions.Item>
            </Descriptions>

            {isCollected && (
              <Alert
                type="error"
                showIcon
                message="Correction directe interdite"
                description="Cette facture a reçu un encaissement. Elle doit être corrigée au moyen d'un avoir afin de préserver le rapprochement du paiement."
                style={{ marginTop: 16 }}
              />
            )}
            {!isCollected && !isHistoricalRegularization && (
              <Alert
                type="warning"
                showIcon
                message="Cette facture n'est pas une régularisation historique"
                style={{ marginTop: 16 }}
              />
            )}
            {isCorrectable && (
              <Alert
                type="warning"
                showIcon
                message="La facture originale sera annulée, jamais modifiée"
                description="Une nouvelle facture numérotée sera émise. Les deux documents resteront liés pour conserver la traçabilité."
                style={{ marginTop: 16 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={submit}
              style={{ marginTop: 20 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="period_start"
                    label="Début de période corrigé"
                    rules={[{ required: true, message: "Date requise" }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      disabledDate={(date) =>
                        date.endOf("day").isAfter(dayjs().endOf("day"))
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="billing_frequency"
                    label="Type de facturation"
                    rules={[{ required: true }]}
                  >
                    <Select options={frequencyOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="due_date"
                    label="Échéance corrigée"
                    rules={[{ required: true, message: "Échéance requise" }]}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="warehouse_count"
                    label="Nombre historique de Warehouses"
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={10000} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="warehouse_unit_price"
                    label="Tarif mensuel historique par Warehouse"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      addonAfter={detail.currency || "XOF"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
                <Descriptions.Item label="Fin de période calculée">
                  {preview.periodEnd
                    ? formatSubscriptionDate(
                        preview.periodEnd.format("YYYY-MM-DD"),
                      )
                    : "À déterminer"}
                </Descriptions.Item>
                <Descriptions.Item label="Remise appliquée">
                  {formatSubscriptionAmount(
                    preview.discountAmount,
                    detail.currency,
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Nouvelle facture TTC">
                  <Text strong>
                    {formatSubscriptionAmount(preview.total, detail.currency)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>

              {values?.billing_frequency === "annual" && (
                <Alert
                  type="info"
                  showIcon
                  message={`La période couvre 12 mois et bénéficie de ${preview.discountedMonths} mois de remise.`}
                  style={{ marginTop: 16 }}
                />
              )}

              <Form.Item
                name="correction_reason"
                label="Motif de la correction"
                rules={[
                  { required: true, whitespace: true, message: "Motif obligatoire" },
                  { min: 3, max: 500, message: "Entre 3 et 500 caractères" },
                ]}
              >
                <TextArea rows={3} maxLength={500} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default SubscriptionInvoiceCorrectionModal;
